import CrownIcon from 'assets/Icons/CrownIcon';
import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  getStructuredPlanData,
  SubscriptionButtonTitle,
} from '@/components/PlatformSubscription/PlanTable/Columns';
import Item from '@/components/PlatformSubscription/PlanTable/ResponsiveTable/Item';

import { user } from '@/atoms/user';
import { IPlan } from '@/utils/interfaces/Subscription';

export interface plansInterface {
  id: number;
  plan: string;
  bestValue: boolean;
  price: string;
  priceCut: string;
  buttonText: string;
  buttonTooltip: string;
  whatInside: string;
  digital: string;
  hard: string;
  duration: string;
  validity: string;
  freeShipping: boolean;
  appAccess: boolean;
  title: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  disabled: boolean;
  videoSummary: boolean;
  audioBook: boolean;
  certification: boolean;
  eBook: string;
  name: string;
  upgradetoHardcopy: string;
  expiresIn: string;
  billed: string;
  monthlyPrice: string;
}

interface Props {
  plans?: IPlan[];
  setSelectedPlan: React.Dispatch<React.SetStateAction<IPlan | undefined>>;
  subscriptionHandle: (buttonTitle: SubscriptionButtonTitle) => Promise<void>;
  isAffiliate?: boolean;
}

const ResponsiveTable: FC<Props> = ({
  plans,
  setSelectedPlan,
  subscriptionHandle,
  isAffiliate: isAffiliateProp,
}) => {
  const userState = useRecoilValue(user);
  const router = useRouter();
  const isAffiliate =
    isAffiliateProp ?? router?.pathname?.includes('affiliate');

  const structuredPlan: plansInterface[] | undefined = plans?.map(
    (plPlan, index) => {
      const planData = getStructuredPlanData(plPlan, userState, 'platform');
      return {
        id: index,
        plan: planData.whatInside,
        title: planData.title,
        bestValue: planData.bestValue,
        price: planData.price,
        priceCut: plPlan.periodicity === 1 ? '2990' : '11,880',
        buttonText: planData.buttonTitle,
        buttonTooltip: planData.buttonTooltip,
        appAccess: planData.appAccess,
        digital: planData.digital,
        hard: planData.hard,
        duration: planData.duration,
        freeShipping: planData.freeShipping,
        validity: planData.validity,
        whatInside: planData.whatInside,
        onClick: () => {
          setSelectedPlan(plPlan);
          plPlan &&
            subscriptionHandle(
              planData.buttonTitle,
            );
        },
        disabled: planData.disabled,
        videoSummary: true,
        audioBook: true,
        certification: true,
        eBook: 'Unlimited Access',
        name: plPlan.name,
        upgradetoHardcopy: plPlan.razorpay_plan?.discountable ? '25% OFF' : '',
        expiresIn: planData.expiresIn,
        billed: planData.billed,
        monthlyPrice:
          planData.monthlyPrice ||
          planData.price ||
          (plPlan?.razorpay_plan?.amount
            ? (plPlan.razorpay_plan.amount / 100).toString()
            : ''),
      };
    },
  );

  // ✅ open all by default
  const [data2, setData2] = useState<number[]>(
    structuredPlan?.map((p) => p.id) || [1],
  );

  return (
    <div className='px-4 py-8'>
      {isAffiliate && (
        <div className='flex flex-col gap-4 mb-8 px-1'>
          <div className='flex items-center gap-3'>
            <CrownIcon width={32} height={32} className='shrink-0' />
            <p className='text-xl font-bold tracking-wide text-[#D3A984] uppercase font-sans'>
              ZEBRALEARN PRIME
            </p>
          </div>

          <div className='flex flex-col gap-2.5'>
            <div className='flex items-center gap-2'>
              <CrownIcon width={16} height={16} className='shrink-0' />
              <p className='text-sm leading-snug'>
                <span className='font-semibold text-white'>No auto-renewal</span>{' '}
                <span className='font-normal text-[#C4C4C4]'>
                  without your consent
                </span>
              </p>
            </div>

            <div className='flex items-center gap-2'>
              <CrownIcon width={16} height={16} className='shrink-0' />
              <p className='text-sm leading-snug'>
                <span className='font-normal text-[#C4C4C4]'>
                  Unlock all app
                </span>{' '}
                <span className='font-semibold text-white'>premium features</span>
              </p>
            </div>

            <div className='flex items-center gap-2'>
              <CrownIcon width={16} height={16} className='shrink-0' />
              <p className='text-sm leading-snug'>
                <span className='font-normal text-[#C4C4C4]'>Access on</span>{' '}
                <span className='font-semibold text-white'>any device</span>
              </p>
            </div>

            <div className='flex items-center gap-2'>
              <CrownIcon width={16} height={16} className='shrink-0' />
              <p className='text-sm leading-snug'>
                <span className='font-semibold text-white'>Unlimited access</span>{' '}
                <span className='font-normal text-[#C4C4C4]'>of books</span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className='flex flex-col gap-4'>
        {structuredPlan?.map((item, index) => (
          <div
            key={index}
            className='border border-grayZl-900 rounded-xl overflow-hidden'
          >
            <div className='flex flex-col gap-1 items-center justify-center px-6 py-3 border-b border-white/60 bg-dropDownMenuBg'>
              <p className='text-lg text-white font-semibold text-center'>
                {item.name == 'Affiliate' ? 'Prime' : item.name}
              </p>
            </div>
            <Item
              index={index}
              item={item}
              setData={setData2}
              data={data2}
              items={structuredPlan}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsiveTable;

